import React, { useState } from 'react';
import { Star, Clock, UserCheck, BookOpen, Award, ChevronDown, ChevronUp, Play } from 'lucide-react';

const CourseDetails = () => {
  const [isContentVisible, setContentVisible] = useState(false);

  const course = {
    id: '1',
    title: 'Advanced Machine Learning Mastery',
    instructor: 'Dr. Jane Smith',
    price: '₹2,499',
    originalPrice: '₹3,999',
    duration: '48 hours',
    level: 'Intermediate',
    rating: 4.8,
    students: 1234,
    description: "Embark on an extraordinary journey into the world of advanced machine learning. This comprehensive course covers cutting-edge topics including deep learning, reinforcement learning, and real-world applications. Gain hands-on experience with state-of-the-art algorithms and transform your career in AI and data science.",
    image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
    topics: ['Neural Networks', 'Deep Learning', 'Reinforcement Learning', 'Computer Vision', 'Natural Language Processing'],
    features: ['24/7 Support', 'Certificate of Completion', 'Lifetime Access', 'Project-Based Learning'],
  };

  const handleEnroll = () => {
    console.log("Enrolled in the course!");
    // Add your enrollment logic here
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white shadow-2xl rounded-3xl overflow-hidden transform transition-all hover:scale-[1.02] duration-300">
          <div className="md:flex">
            <div className="md:flex-shrink-0 md:w-1/2">
              <img className="h-full w-full object-cover" src={course.image} alt={course.title} />
            </div>
            <div className="p-8 md:w-1/2">
              <div className="uppercase tracking-wide text-sm text-indigo-600 font-semibold">{course.level}</div>
              <h1 className="mt-2 text-4xl leading-tight font-extrabold text-gray-900 sm:text-5xl group">
                {course.title}
                <span className="block mt-2 text-3xl text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-pink-600 group-hover:bg-gradient-to-l transition-all duration-300">
                  Master the Future of AI
                </span>
              </h1>
              <p className="mt-4 text-xl text-gray-500">by {course.instructor}</p>
              <div className="mt-4 flex items-center">
                <Star className="h-6 w-6 text-yellow-400" />
                <span className="ml-2 text-lg font-medium text-gray-900">{course.rating}</span>
                <span className="ml-2 text-lg text-gray-600">({course.students.toLocaleString()} students)</span>
              </div>
              <div className="mt-4 flex items-center text-lg text-gray-600">
                <Clock className="h-6 w-6 mr-2" />
                {course.duration}
              </div>
              <div className="mt-6">
                <span className="text-5xl font-bold text-gray-900">{course.price}</span>
                <span className="ml-2 text-2xl font-medium text-gray-500 line-through">{course.originalPrice}</span>
              </div>
              <div className="mt-8 space-y-4">
                <button
                  onClick={handleEnroll}
                  className="w-full flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-full text-white bg-gradient-to-r from-indigo-600 to-pink-600 hover:from-indigo-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transform transition-all hover:scale-105 duration-300"
                >
                  Enroll Now
                </button>
                <p className="text-center text-gray-600">30-Day Money-Back Guarantee</p>
              </div>
            </div>
          </div>
          <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Course Overview</h2>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">{course.description}</p>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                  <BookOpen className="h-6 w-6 mr-2 text-indigo-600" />
                  What you'll learn
                </h3>
                <ul className="space-y-3">
                  {course.topics.map((topic, index) => (
                    <li key={index} className="flex items-center text-lg text-gray-700">
                      <Play className="h-5 w-5 mr-2 text-green-500" />
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Award className="h-6 w-6 mr-2 text-indigo-600" />
                  This course includes
                </h3>
                <ul className="space-y-3">
                  {course.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-lg text-gray-700">
                      <UserCheck className="h-5 w-5 mr-2 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-8">
              <button
                onClick={() => setContentVisible(!isContentVisible)}
                className="flex items-center justify-between w-full text-left text-xl font-semibold text-gray-900 focus:outline-none"
              >
                <span>View Detailed Course Content</span>
                {isContentVisible ? (
                  <ChevronUp className="h-6 w-6 text-indigo-600" />
                ) : (
                  <ChevronDown className="h-6 w-6 text-indigo-600" />
                )}
              </button>
              {isContentVisible && (
                <div className="mt-4 space-y-6 text-gray-700">
                  <div>
                    <h4 className="text-xl font-semibold mb-2">Module 1: Foundations of Advanced ML</h4>
                    <p>Dive deep into the core principles that drive modern machine learning algorithms.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">Module 2: Deep Learning Architectures</h4>
                    <p>Explore the intricacies of neural networks and their groundbreaking applications.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">Module 3: Reinforcement Learning</h4>
                    <p>Master the techniques that power decision-making in complex, dynamic environments.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">Module 4: Advanced Computer Vision</h4>
                    <p>Learn to build sophisticated systems that can interpret and analyze visual information.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">Module 5: Natural Language Processing at Scale</h4>
                    <p>Develop cutting-edge models for understanding and generating human language.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">Module 6: Capstone Project</h4>
                    <p>Apply your skills to a real-world problem, showcasing your expertise to potential employers.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetails;